import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { GalleryCarousel } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PresaleDetailsTab/GalleryCarousel'
import { getGiveawayDetailsContent } from '@/views/PreSales/PreSalesGiveawayView/config/giveaway-details-content'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GalleryImage, GiveawayPrizeItem } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'
import { usePrice } from '@/views/TicketCheckoutViews/hooks/usePrice'
import { GButtonSM, GParagraphMD, GParagraphSM, GResponsiveText, GTitleXL } from '../../UI/Typography'

interface Props {
  prizes?: {
    grandPrizes?: GiveawayPrizeItem[]
    firstPlacePrizes?: GiveawayPrizeItem[]
    prizes?: GiveawayPrizeItem[]
  } | null
  galleryImages: GalleryImage[]
  trailerId: string | null | undefined
  theatricalSlug: string
  projectName: SweepstakeObject['projectName']
  videoImage?: string
  giveawayValue?: SweepstakeObject['value']
}

export const PresaleDetailsTab: FC<Props> = ({
  prizes,
  galleryImages,
  trailerId,
  theatricalSlug,
  videoImage,
  giveawayValue,
}) => {
  const { t } = useTranslate('tickets')
  const [loadPlayer, setLoadPlayer] = useState(!videoImage)
  const { track } = useSafeAnalytics()
  const giveawayPrice = usePrice(giveawayValue, { minimumFractionDigits: 0 })
  const { experiments, scrollToPrizes } = useGiveawayContext()

  const detailsContent = getGiveawayDetailsContent(theatricalSlug)

  const isSingleTab = experiments?.['young-washington-single-tab']
  const grandPrizes = prizes?.grandPrizes

  return (
    <FlexColumn
      className={classNames(
        isSingleTab ? 'w-full px-4' : 'mb-16 w-full gap-16 px-4 pt-16 md:gap-[72px] md:px-9 md:pb-32',
      )}
    >
      <div className="flex w-full flex-col gap-1">
        {trailerId && (
          <div
            id="youtube"
            style={{ aspectRatio: 16 / 9 }}
            className="relative my-3 w-full max-w-[361px] overflow-hidden rounded-[10px] md:mb-[72px] md:max-w-[530px]"
          >
            {!loadPlayer && videoImage && (
              <div
                className="cursor-pointer"
                onClick={() => {
                  track('Giveaway Video Clicked')
                  setLoadPlayer(true)
                }}
              >
                <Image
                  alt="Video player thumbnail"
                  src={videoImage}
                  quality={100}
                  fill
                  className="object-cover object-center"
                />
                <div className="absolute left-1/2 top-1/2 flex h-fit w-fit -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white p-4">
                  <PlayIcon size={32} color="core-gray-950" />
                </div>
              </div>
            )}
            {loadPlayer && (
              <EmbeddedVideo
                url={`https://www.youtube-nocookie.com/embed/${trailerId}?autoplay=1`}
                className="mx-auto !rounded-[10px] md:mx-0"
              />
            )}
          </div>
        )}

        {detailsContent}

        {theatricalSlug === slugs.sketch && <></>}
      </div>
      {theatricalSlug === slugs.sketch && grandPrizes && (
        <section>
          <GTitleXL color="giveaway-text-primary-dark" className="mb-1">
            {t('grandPrizeItems', 'Grand Prize Items')}
          </GTitleXL>
          {giveawayValue && (
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mb-6"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {t('giveawayGrandValueString', 'This Grand Prize giveaway package is valued at over {{giveawayValue}}.', {
                giveawayValue: giveawayPrice,
              })}
            </GResponsiveText>
          )}

          <ul className="ml-5 list-[disc]">
            {grandPrizes.slice(0, 5).map((prizeItem, index) => (
              <li key={index} className="my-1 !text-giveaway-text-subtle-light">
                <GParagraphSM>
                  {prizeItem.title} ({prizeItem.value})
                </GParagraphSM>
              </li>
            ))}
          </ul>
          <Button
            className="mt-6 w-full rounded-none border-2 !border-giveaway-primary-accent py-[14.5px] md:w-fit"
            variant="white"
            outline
            onClick={scrollToPrizes}
          >
            <GButtonSM color="giveaway-text-subtle-light">{t('showAllGiveawayItems', 'Show all Items')}</GButtonSM>
          </Button>
        </section>
      )}

      {galleryImages.length >= 1 &&
        theatricalSlug !== slugs.senior &&
        (isSingleTab || theatricalSlug !== slugs.youngWashington) && (
          <GalleryCarousel galleryImages={galleryImages} theatricalSlug={theatricalSlug} />
        )}
    </FlexColumn>
  )
}
